import { useEffect, useRef, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import SplitType from "split-type";

gsap.registerPlugin(ScrollTrigger);

export const useTextAnimation = (isLoaded = true) => {
  const textRef = useRef([]);
  const headingRef = useRef(null);
  const subtitleRef = useRef([]);
  const bodyRef = useRef([]);

  useEffect(() => {
      if (!isLoaded) return;

      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.inOut", delay: 0 }
        );
      }

      subtitleRef.current.forEach((ref, index) => {
        if (ref) {
          gsap.fromTo(
            ref,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 1, ease: "power2.inOut", delay: 0.1 + index * 0.2 }
          );
        }
      });

      bodyRef.current.forEach((ref, index) => {
        if (ref) {
          gsap.fromTo(
            ref,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 1, ease: "power2.inOut", delay: 0.3 + index * 0.2 }
          );
        }
      });
    }, [isLoaded]);

  useEffect(() => {
    setTimeout(() => {
      document.body.classList.add("page-loaded");
      ScrollTrigger.refresh();
    }, 500);
  }, []);

  useEffect(() => {
    const ensureTextRefReady = () => {
      return new Promise((resolve) => {
        if (textRef.current.every((ref) => ref && ref.offsetHeight > 0)) {
          resolve();
        } else {
          setTimeout(() => ensureTextRefReady().then(resolve), 100);
        }
      });
    };

    ensureTextRefReady().then(() => {
      textRef.current.forEach((ref) => {
        if (!ref) return;
        const splitText = new SplitType(ref, { 
          types: "words",
          wordClass: "split-word"
        });

        // Reset inline color SplitType writes
        splitText.words.forEach(word => {
          word.style.color = '';
        });

        gsap.fromTo(
          splitText.words,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1.5,
            stagger: 0.05,
            scrollTrigger: {
              trigger: ref,
              start: "top 90%",
              end: "top 50%",
              scrub: 1.5,
              onEnter: () => gsap.set(ref, { visibility: "visible" }),
            },
          }
        );
      });

      ScrollTrigger.refresh();
    });
  }, []);

  return { textRef, headingRef, subtitleRef, bodyRef };
};


export const useHeroTextAnimation = () => {
  const captionRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  const animateIn = useCallback(() => {
    [captionRef, titleRef, descriptionRef].forEach((ref, index) => {
      if (!ref.current) return;

      gsap.killTweensOf(ref.current);

      gsap.fromTo(
        ref.current,
        { opacity: 0, y: 10 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          delay: 0.5 + index * 0.1,
        }
      );
    });
  }, []); 

  return { captionRef, titleRef, descriptionRef, animateIn };
};
